import { useMemo } from "react";
import Letterhead from "@/components/Letterhead";
import RxWatermark from "@/components/RxWatermark";

/**
 * REQUIRED EXPORTS (DO NOT REMOVE - USED BY OTHER MODULES)
 */
export const DEFAULT_TEMPLATE = `To Whom It May Concern:

This is to certify that {{patient_name}}, {{age}} years old, {{gender}}, from {{address}}, was seen and examined in my clinic on {{date}}, with the following diagnosis:

{{diagnosis}}

Recommendations:
{{he_she}} is advised to take {{rest_days}} day(s) of rest.
{{recommendations}}

{{remarks}}
`;

export const VACCINE_TEMPLATE = `To Whom It May Concern:

This certifies that {{patient_name}}, {{age}} years old, has received vaccination on {{date}}.

Details:
{{remarks}}

This serves as official medical documentation.
`;

/**
 * HELPERS
 */
function safe(v, fb = "") {
  return v === undefined || v === null || v === "" ? fb : v;
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-PH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function pronoun(gender, maleWord = "He", femaleWord = "She", fallback = "He/She") {
  const g = String(gender || "").toLowerCase();
  if (g.includes("female") || g === "f") return femaleWord;
  if (g.includes("male") || g === "m") return maleWord;
  return fallback;
}

function patientAddress(patient = {}) {
  return (
    patient.address ||
    patient.complete_address ||
    patient.full_address ||
    patient.home_address ||
    [patient.street, patient.barangay, patient.city, patient.province]
      .filter(Boolean)
      .join(", ")
  );
}

function generateSerialNumber(certificateId) {
  const year = new Date().getFullYear();
  const base = String(certificateId || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  const src = (base + "0000000000000000000000000").slice(0, 20);
  return `CERT-${year}-${src.slice(0, 5)}-${src.slice(5, 10)}-${src.slice(10, 15)}`;
}

/**
 * TEMPLATE ENGINE
 */
function renderTemplate(template, certificate, patient, issueDate) {
  const values = {
    patient_name: safe(patient.full_name, "________________"),
    age: safe(patient.age, "____"),
    gender: safe(patient.gender, "male/female"),
    address: safe(patientAddress(patient), "________________"),
    // A free-text date override wins over the formatted issue date.
    date: (certificate?.date_text || "").trim() || fmtDate(issueDate),
    // Wrapped in a sentinel (U+E000) so the renderer can print the diagnosis in bold.
    diagnosis: `${safe(certificate?.diagnosis, "________________")}`,
    recommendations: safe(certificate?.recommendations, ""),
    remarks: safe(certificate?.remarks, ""),
    purpose: safe(certificate?.purpose, ""),
    rest_days: certificate?.rest_days || "___",
    he_she: pronoun(patient?.gender),
  };

  let out = template || DEFAULT_TEMPLATE;

  Object.entries(values).forEach(([k, v]) => {
    out = out.replaceAll(`{{${k}}}`, String(v));
  });

  return out;
}

/**
 * MAIN COMPONENT
 */
export default function MedicalCertificatePreview({
  certificate,
  settings = {},
  template,
}) {
  const issueDate = certificate?.issue_date || new Date().toISOString();

  // Same letterhead as the Rx: read the doctor snapshot saved on the certificate.
  const doctor = certificate?.doctor_snapshot || certificate?.doctor || {};
  const affiliations =
    Array.isArray(doctor.affiliations) && doctor.affiliations.length
      ? doctor.affiliations
      : Array.isArray(settings.affiliations)
      ? settings.affiliations
      : [];
  const isMulti = (doctor.layout_template || settings.layout_template) === "multi_clinic";

  const content = useMemo(() => {
    const patient = certificate?.patient || certificate?.patient_snapshot || {};
    // Prefer an explicit template prop, else the certificate's own (doctor-edited)
    // body_template, else the built-in default. This is what makes the doctor's
    // edits actually show on the preview and the saved/printed certificate.
    // Trim the rendered body: older issued certificates stored templates that
    // begin with a blank line, which printed as dead space above the greeting —
    // and an empty {{remarks}} used to leave stray blank lines at the bottom.
    return renderTemplate(template || certificate?.body_template, certificate, patient, issueDate)
      .replace(/^\s+/, "").replace(/\s+$/, "");
  }, [certificate, template, issueDate]);

  const showHeader = settings.show_doctor_header !== false && !settings.template_image_url;

  // Honour the hospital's configured paper size (same as the Rx / referral).
  const PAPER = { A4: "size-A4", Letter: "size-Letter", Half: "size-Half" };
  const paperCls = PAPER[settings.paper_size] || "size-A4";
  const pageStyle = settings.paper_size === "Custom"
    ? { width: `${settings.custom_width || 210}mm`, minHeight: `${settings.custom_height || 297}mm` }
    : {};

  return (
    <div className={`print-area rx-paper allow-break ${paperCls}`} data-testid="medcert-preview" style={{ position: "relative", ...pageStyle }}>
      <RxWatermark doctor={doctor} settings={settings} />
      {/* Table layout so the letterhead (thead) repeats on every printed page. */}
      <table style={{ width: "100%", borderCollapse: "collapse", position: "relative", zIndex: 1 }}>
        <thead className="rx-repeat-header">
          <tr><td style={{ padding: "12mm 15mm 0 15mm" }}>
            {showHeader ? <Letterhead doctor={doctor} s={settings} affiliations={affiliations} isMulti={isMulti} /> : null}
          </td></tr>
        </thead>
        <tbody>
          <tr><td style={{ padding: "6mm 15mm 15mm 15mm", verticalAlign: "top" }}>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                fontFamily: "Times New Roman",
                fontSize: "11pt",
                lineHeight: 1.4,
                margin: 0,
              }}
            >
              {/* Odd segments are the diagnosis (wrapped in the U+E000 sentinel) → bold. */}
              {content.split("").map((seg, i) =>
                i % 2 === 1 ? <strong key={i}>{seg}</strong> : <span key={i}>{seg}</span>
              )}
            </pre>

            {/* Standard PH disclaimer — rendered by the component, not stored in
                the editable template, so it appears on every certificate (old and
                new) and cannot be edited away. print-keep: a legal rider split
                across a page break reads as two half-sentences. */}
            <div
              className="print-keep"
              style={{
                marginTop: "8mm",
                fontFamily: "Times New Roman",
                fontSize: "10.5pt",
                lineHeight: 1.4,
                fontStyle: "italic",
              }}
              data-testid="medcert-disclaimer"
            >
              ***This medical certificate is being issued upon patient’s request for whatever
              purpose it may serve <strong>EXCEPT MEDICO-LEGAL PURPOSES</strong>.
            </div>

            {/* Doctor signature block (kept together so it never splits across pages). */}
            <div className="print-keep" style={{ marginTop: "16mm", display: "flex", justifyContent: "flex-end" }}>
              <div style={{ textAlign: "left", width: "78mm" }} data-testid="medcert-signature">
                {(doctor.signature_image_url || settings.signature_image_url) ? (
                  <img
                    src={doctor.signature_image_url || settings.signature_image_url}
                    alt="signature"
                    style={{ maxHeight: "14mm", marginBottom: "-4px" }}
                  />
                ) : (
                  <div style={{ height: "12mm" }} />
                )}
                <div style={{ borderTop: "1px solid #0f172a", paddingTop: "1mm", fontFamily: "Arial, sans-serif" }}>
                  <div style={{ fontWeight: 700, textTransform: "uppercase", fontSize: "10pt" }}>
                    {safe(doctor.name || doctor.doctor_name, "") || safe(settings.doctor_name) || "DOCTOR NAME, MD"}
                  </div>
                  {safe(doctor.specialty, settings.specialty) ? (
                    <div style={{ fontSize: "9pt", color: "#334155" }}>{safe(doctor.specialty, settings.specialty)}</div>
                  ) : null}
                  <div style={{ fontSize: "9pt" }}>Lic. No.: {safe(doctor.license_number, settings.license_number) || "—"}</div>
                  <div style={{ fontSize: "9pt" }}>PTR No.: {safe(doctor.ptr_number, settings.ptr_number) || "—"}</div>
                  {doctor.s2_number ? <div style={{ fontSize: "9pt" }}>S2 No.: {doctor.s2_number}</div> : null}
                </div>
              </div>
            </div>

            {/* Serial number footer */}
            <div
              className="print-keep"
              style={{
                marginTop: "6mm",
                paddingTop: "2mm",
                borderTop: "1px solid #e2e8f0",
                textAlign: "center",
                fontFamily: "Arial, sans-serif",
                fontSize: "7.5pt",
                color: "#64748b",
              }}
              data-testid="medcert-serial"
            >
              {generateSerialNumber(certificate?.id)}
            </div>
          </td></tr>
        </tbody>
      </table>
    </div>
  );
}
